import React, { FC, ReactNode, useState } from "react";
import { Helmet }                         from "react-helmet";

import { makeStyles }          from "@material-ui/core/styles";
import CssBaseline             from "@material-ui/core/CssBaseline";
import AppBar                  from "@material-ui/core/AppBar";
import Toolbar                 from "@material-ui/core/Toolbar";
import Typography              from "@material-ui/core/Typography";
import Container               from "@material-ui/core/Container";
import Menu                    from '@material-ui/core/Menu';
import MenuItem                from '@material-ui/core/MenuItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText            from '@material-ui/core/ListItemText';
import Button                  from "@material-ui/core/Button";
import IconButton                  from "@material-ui/core/IconButton";
import DialogTitle             from "@material-ui/core/DialogTitle";
import DialogContent           from "@material-ui/core/DialogContent";
import DialogActions           from "@material-ui/core/DialogActions";
import Dialog                  from "@material-ui/core/Dialog";

import LanguageIcon            from '@material-ui/icons/Language';
import DoneIcon                from '@material-ui/icons/Done';
import InfoIcon                 from '@material-ui/icons/Info';

import { FontInfo, LanguageMap, Logo } from "../types";
import { useLocaleInfo }               from "../hooks/lanugage";
import { FormattedMessage }            from "react-intl";
import ReactMarkdown                   from "react-markdown";
import { Info }                        from "@material-ui/icons";


export interface RootProps {
    favicon: string;
    info?: LanguageMap;
    title: LanguageMap;
    logo: Logo;
    name: LanguageMap;
    children: ReactNode;
    fonts: FontInfo[];
}

const centeredBackgroundImage = {
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "center",
}

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1
    },
    container: {
        marginTop: 84
    },
    spacer: {
      flexGrow: 1
    },
    largeLogo: {
        ...centeredBackgroundImage,
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block'
        }
    },
    smallLogo: {
        ...centeredBackgroundImage,
        display: 'none',
        minWidth: 32,
        minHeight: 32,
        maxHeight: 32,
        maxWidth: 100,
        [theme.breakpoints.down('xs')]: {
            display: 'block'
        }
    },
    title: {
        fontSize: '1.5em',
        [theme.breakpoints.down('sm')]: {
            fontSize: '1em'
        },
        [theme.breakpoints.down('xs')]: {
            display: 'none'
        }
    }
}))

const createFont = (font: FontInfo): string => {
    return `
@font-face {
  font-family: ${ font.name };
  src: ${ font.src };
}
    `;
}

export const RootLayout: FC<RootProps> = ({ children, title, logo, name, favicon, fonts, info }) => {
    const classes = useStyles();
    const { languageKey, languageName, languages, setLanguage } = useLocaleInfo();

    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [infoOpen, setInfoOpen] = useState<boolean>(!localStorage.getItem('@info'));

    const updateLanguage = (key: string) => () => {
        setLanguage(key);
        handleClose();
    }

    const handleClick = (event: React.MouseEvent) => {
        setAnchorEl(event.currentTarget as Element);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleCloseInfo = () => {
        localStorage.setItem('@info', 'true');
        setInfoOpen(false);
    }

    const handleOpenInfo = () => {
        setInfoOpen(true);
    }

    return (
        <>
            <Helmet>
                <title>{ title[languageKey] }</title>
                <link rel="shortcut icon" type="image/x-icon" href={ favicon }/>
                <style id='template-fonts'>
                    {
                        fonts.map(createFont).join('\n')
                    }
                </style>
            </Helmet>
            {
                info &&
                <Dialog open={ infoOpen } onClose={ handleCloseInfo }>
                    <DialogTitle>
                        <FormattedMessage id={ 'dashboard.info.title' }/>
                    </DialogTitle>
                    <DialogContent>
                        <ReactMarkdown>
                            { info[languageKey] }
                        </ReactMarkdown>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={ handleCloseInfo }
                            variant="contained"
                            color="secondary"
                            autoFocus
                        >
                            <FormattedMessage id={ 'inputs.dialog.ok' }/>
                        </Button>
                    </DialogActions>
                </Dialog>
            }
            <div className={ classes.root }>
                <div style={ { position: 'fixed', zIndex: -1, opacity: 0 } }>
                    {
                        fonts.map(f => {
                            return <div key={ f.name } style={ { fontFamily: f.name } }>
                                { f.name }
                            </div>
                        })
                    }
                </div>
                <CssBaseline/>
                <AppBar position={ 'fixed' }>
                    <Toolbar>
                        <div className={ classes.largeLogo } style={ {
                            backgroundImage: `url(${ logo.path })`,
                            width: `${ logo.width }px`,
                            height: `${ logo.height }px`,
                            marginRight: logo.margin && `${ logo.margin }px`
                        } }/>
                        <div className={ classes.smallLogo } style={ {
                            backgroundImage: `url(${ logo.smallPath || logo.path })`,
                            width: `${ logo.width }px`,
                            height: `${ logo.height }px`,
                            marginRight: logo.margin && `${ logo.margin }px`
                        } }/>
                        <Typography className={ classes.title }>{ name[languageKey] }</Typography>
                        <span className={classes.spacer} />
                        {
                            info &&
                            <IconButton onClick={ handleOpenInfo } color={ 'inherit' }>
                                <InfoIcon />
                            </IconButton>
                        }
                        <Button onClick={ handleClick } color={ 'inherit' }>
                            <LanguageIcon/>
                            { languageName }
                        </Button>
                        <Menu
                            anchorEl={ anchorEl }
                            keepMounted
                            open={ Boolean(anchorEl) }
                            onClose={ handleClose }
                        >
                            {
                                languages.map(lang => {
                                    return (
                                        <MenuItem key={ lang.key } onClick={ updateLanguage(lang.key) } button>
                                            <ListItemText>
                                                { lang.name }
                                            </ListItemText>
                                            <ListItemSecondaryAction>
                                                {
                                                    lang.key === languageKey &&
                                                    <DoneIcon fontSize={ 'small' }/>
                                                }
                                            </ListItemSecondaryAction>
                                        </MenuItem>
                                    )
                                })
                            }
                        </Menu>
                    </Toolbar>
                </AppBar>
                <Container className={ classes.container }>
                    { children || <div/> }
                </Container>
            </div>
        </>
    )
}